import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as sppoCreationAPI from '../../api/SpPOAPI/sppoCreationAPI';

// Async Thunks
// =============================================

export const fetchVendorsForSPPO = createAsyncThunk(
    'sppoCreation/fetchVendorsForSPPO',
    async (_, { rejectWithValue }) => {
        try { return await sppoCreationAPI.getVendorsForSPPO(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Vendors'); }
    }
);

export const fetchBudgetCCForSPPO = createAsyncThunk(
    'sppoCreation/fetchBudgetCCForSPPO',
    async (_, { rejectWithValue }) => {
        try { return await sppoCreationAPI.getBudgetCCForSPPO(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers'); }
    }
);

export const fetchDCAByCCForSPPO = createAsyncThunk(
    'sppoCreation/fetchDCAByCCForSPPO',
    async (ccCode, { rejectWithValue }) => {
        try { return await sppoCreationAPI.getDCAByCCForSPPO(ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Account Heads'); }
    }
);

export const fetchSubDCAbyDCA = createAsyncThunk(
    'sppoCreation/fetchSubDCAbyDCA',
    async (dcaCode, { rejectWithValue }) => {
        try { return await sppoCreationAPI.getSubDCAbyDCA(dcaCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Sub Account Heads'); }
    }
);

export const fetchUpdloadDocSPPOValue = createAsyncThunk(
    'sppoCreation/fetchUpdloadDocSPPOValue',
    async (ccCode, { rejectWithValue }) => {
        try { return await sppoCreationAPI.getUpdloadDocSPPOValue(ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch upload threshold'); }
    }
);

export const saveNewServiceProvider = createAsyncThunk(
    'sppoCreation/saveNewServiceProvider',
    async (payload, { rejectWithValue }) => {
        try { return await sppoCreationAPI.saveNewServiceProvider(payload); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to save Service Provider PO'); }
    }
);

// Initial State
// =============
const initialState = {
    vendors: [],
    costCenters: [],
    dcaList: [],
    subDcaList: [],
    uploadValue: 0,

    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'

    loading: {
        vendors: false,
        costCenters: false,
        dcaList: false,
        subDcaList: false,
        uploadValue: false,
        save: false,
    },

    errors: {
        vendors: null,
        costCenters: null,
        dcaList: null,
        subDcaList: null,
        uploadValue: null,
        save: null,
    },
};

const sppoCreationSlice = createSlice({
    name: 'sppoCreation',
    initialState,
    reducers: {
        clearDCAList: (state) => { state.dcaList = []; state.errors.dcaList = null; },
        clearSubDCAList: (state) => { state.subDcaList = []; state.errors.subDcaList = null; },
        clearSaveResult: (state) => { state.saveResult = null; state.saveStatus = null; state.errors.save = null; },
        resetSPPOCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVendorsForSPPO.pending, (state) => { state.loading.vendors = true; state.errors.vendors = null; })
            .addCase(fetchVendorsForSPPO.fulfilled, (state, action) => { state.loading.vendors = false; state.vendors = action.payload?.Data || []; })
            .addCase(fetchVendorsForSPPO.rejected, (state, action) => { state.loading.vendors = false; state.errors.vendors = action.payload; state.vendors = []; });

        builder
            .addCase(fetchBudgetCCForSPPO.pending, (state) => { state.loading.costCenters = true; state.errors.costCenters = null; })
            .addCase(fetchBudgetCCForSPPO.fulfilled, (state, action) => { state.loading.costCenters = false; state.costCenters = action.payload?.Data || []; })
            .addCase(fetchBudgetCCForSPPO.rejected, (state, action) => { state.loading.costCenters = false; state.errors.costCenters = action.payload; state.costCenters = []; });

        builder
            .addCase(fetchDCAByCCForSPPO.pending, (state) => { state.loading.dcaList = true; state.errors.dcaList = null; })
            .addCase(fetchDCAByCCForSPPO.fulfilled, (state, action) => { state.loading.dcaList = false; state.dcaList = action.payload?.Data || []; })
            .addCase(fetchDCAByCCForSPPO.rejected, (state, action) => { state.loading.dcaList = false; state.errors.dcaList = action.payload; state.dcaList = []; });

        builder
            .addCase(fetchSubDCAbyDCA.pending, (state) => { state.loading.subDcaList = true; state.errors.subDcaList = null; })
            .addCase(fetchSubDCAbyDCA.fulfilled, (state, action) => { state.loading.subDcaList = false; state.subDcaList = action.payload?.Data || []; })
            .addCase(fetchSubDCAbyDCA.rejected, (state, action) => { state.loading.subDcaList = false; state.errors.subDcaList = action.payload; state.subDcaList = []; });

        builder
            .addCase(fetchUpdloadDocSPPOValue.pending, (state) => { state.loading.uploadValue = true; state.errors.uploadValue = null; })
            .addCase(fetchUpdloadDocSPPOValue.fulfilled, (state, action) => {
                state.loading.uploadValue = false;
                const data = action.payload?.Data || [];
                state.uploadValue = data.length > 0 ? (data[0].uploadvalue || 0) : 0;
            })
            .addCase(fetchUpdloadDocSPPOValue.rejected, (state, action) => { state.loading.uploadValue = false; state.errors.uploadValue = action.payload; state.uploadValue = 0; });

        builder
            .addCase(saveNewServiceProvider.pending, (state) => { state.loading.save = true; state.errors.save = null; state.saveStatus = 'pending'; })
            .addCase(saveNewServiceProvider.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split('$');
                if (status === 'Submited') { state.saveStatus = 'success'; }
                else { state.saveStatus = 'failed'; state.errors.save = responseStr || 'Save failed'; }
            })
            .addCase(saveNewServiceProvider.rejected, (state, action) => { state.loading.save = false; state.errors.save = action.payload; state.saveStatus = 'failed'; state.saveResult = null; });
    },
});

export const {
    clearDCAList,
    clearSubDCAList,
    clearSaveResult,
    resetSPPOCreation,
} = sppoCreationSlice.actions;

// Selectors
// =========
const arr = (x) => (Array.isArray(x) ? x : []);

export const selectVendorsArray = (state) => arr(state.sppoCreation.vendors);
export const selectVendorsLoading = (state) => state.sppoCreation.loading.vendors;

export const selectCostCentersArray = (state) => arr(state.sppoCreation.costCenters);
export const selectCostCentersLoading = (state) => state.sppoCreation.loading.costCenters;

export const selectDCAListArray = (state) => arr(state.sppoCreation.dcaList);
export const selectDCAListLoading = (state) => state.sppoCreation.loading.dcaList;

export const selectSubDCAListArray = (state) => arr(state.sppoCreation.subDcaList);
export const selectSubDCAListLoading = (state) => state.sppoCreation.loading.subDcaList;

export const selectUploadValue = (state) => state.sppoCreation.uploadValue;

export const selectSaveStatus = (state) => state.sppoCreation.saveStatus;
export const selectSaveLoading = (state) => state.sppoCreation.loading.save;
export const selectSaveError = (state) => state.sppoCreation.errors.save;

export default sppoCreationSlice.reducer;
